/* Entry motion for the previews of the system.
   - useCountUp: every element marked data-num inside a root counts from 0 to the number it shows, once, when `play`
     turns true. The text is parsed, never changed in format: "19.6 MNOK" → 0.0 … 19.6 MNOK, "58%" → 0 … 58%, "+2.3 MNOK".
     Honours prefers-reduced-motion (then nothing moves). The real app never calls this – only the mockups do. */
import { useEffect } from "react";

const reduced = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* "+2.3 MNOK" → { sign: "+", int: "2", sep: ".", frac: "3", rest: " MNOK" } · null when the text is not a plain number */
const parse = (text) => {
  const m = /^(\s*[+−-]?)(\d{1,6})(?:([.,])(\d{1,3}))?(\s*[^\d]*)$/.exec(text);
  if (!m) return null;
  const [, sign, int, sep, frac, rest] = m;
  const value = parseFloat(`${int}.${frac || "0"}`);
  if (!Number.isFinite(value) || value === 0) return null;
  return { sign, sep: sep || "", decimals: frac ? frac.length : 0, rest, value, text };
};

const easeOut = (t) => 1 - Math.pow(1 - t, 4);

/* lowest text node inside el that carries the number (data-num sits on the element that shows it) */
const textNode = (el) => {
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let n;
  let found = null;
  while ((n = walk.nextNode())) {
    if (/\d/.test(n.textContent)) found = n;
  }
  return found;
};

export const useCountUp = (ref, play, { delay = 450, duration = 1500, stagger = 80 } = {}, deps = []) => {
  useEffect(() => {
    if (!play) return undefined;
    const root = ref.current;
    if (!root || reduced()) return undefined;
    const items = [];
    root.querySelectorAll("[data-num]").forEach((el, i) => {
      if (el.dataset.counted) return;
      const node = textNode(el);
      if (!node) return;
      const p = parse(node.textContent);
      if (!p) return;
      el.dataset.counted = "1";
      items.push({ el, node, p, start: delay + i * stagger, full: node.textContent });
    });
    if (!items.length) return undefined;
    /* show zero right away so the count visibly starts from nothing */
    items.forEach(({ node, p }) => {
      node.textContent = `${p.sign}${(0).toFixed(p.decimals).replace(".", p.sep || ".")}${p.rest}`;
    });
    let raf = 0;
    const t0 = performance.now();
    const tick = (now) => {
      let live = false;
      items.forEach((it) => {
        const t = (now - t0 - it.start) / duration;
        if (t >= 1) {
          if (it.node.textContent !== it.full) it.node.textContent = it.full;
          return;
        }
        live = true;
        const v = t <= 0 ? 0 : it.p.value * easeOut(t);
        it.node.textContent = `${it.p.sign}${v.toFixed(it.p.decimals).replace(".", it.p.sep || ".")}${it.p.rest}`;
      });
      if (live) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      items.forEach((it) => {
        it.node.textContent = it.full;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, play, delay, duration, stagger, ...deps]);
};
